import React, { useState, useEffect } from 'react';
import { RecommendedAccessories } from '@hpstellar/core';

import { Helpers } from '../../../core/src/helpers';
import useCartState from '../../../hooks/useCartState';
import useProductPrice from '../../../hooks/useProductPrice';
import useProductImpressions from '../../../hooks/useProductImpressionsV2';
import { useAttachableProductProps } from '../../../hooks/useProductProps';
import { getIcon } from '../../../category-landing-page/components/shared';
import { getPriceMetricInput, formatMetricValue } from '../../../metrics/metrics-helpers';
import { useDynamicYieldSort } from '../../../hooks/useDynamicYieldRecs';
import { mapInstockProducts } from './util';

import useTemplateTheme from '../../../hooks/useTemplateTheme';

import './cross-sell-omen-brand.less';

const mapCategoryIcons = categories => {
    return categories.map(({ title, id, iconName }) => {
        return {
            title,
            id,
            icon: getIcon(id),
        };
    });
};

const mapAccessories = Helpers.memoize(
    (products, prices = {}, baseProduct, getAttachableProductProps, cartId, list, xsellMethod) => {
        return mapInstockProducts(products, prices, (p, price) => {
            return getAttachableProductProps(p, price, undefined, undefined, {
                xsellProdInfo: baseProduct && baseProduct.sku,
                xsellMethod,
                cartId,
                list,
                position: (p && p.position) || i + 1,
            });
        });
    },
    (products, prices) => {
        return (products || []).map(p => `${p?.sku in prices}-${p?.sku}`).join('-');
    },
);

const onAdd = relatedProducts => sku => {
    const product = relatedProducts.filter(product => product.sku === sku)[0];
    product.onAttach();
};

const AccessoriesComponent = ({
    relatedProducts,
    categories,
    onCategoryChange,
    title,
    productInitial,
    selectedCat,
}) => {
    const [getAttachableProductProps] = useAttachableProductProps();
    const { prices } = useProductPrice(relatedProducts, { returnEmptyPriceObj: false });
    const { cartId } = useCartState();
    const list = 'pdp';
    const selectedCategory = Array.isArray(categories) ? categories.find(cat => cat && cat.id === selectedCat) : null;
    const { title: selectedCatTitle } = selectedCategory || {};
    const xsellMethod = `pdp-accessories-${formatMetricValue(selectedCatTitle)}`;
    const relatedProductsWithAnalytics = useProductImpressions({
        products: relatedProducts,
        list,
        componentKey: 'pdpAccessories',
        impressionKey: selectedCat,
        isEmpty: relatedProducts && relatedProducts.length === 0,
        trackMetrics: true,
        preventRepeatingImpressions: true,
        customPrices: getPriceMetricInput(relatedProducts, prices),
    });

    // const { css , props: themeProps } = {
    //     props: {
    //         classes: {
    //             tabActive: 'omen-dark-tab-active',
    //             tab: 'omen-dark-tab',
    //             desktopTitle: 'omen-dark-desktop-title',
    //             accordionTitle: 'omen-dark-accordion-title',
    //             tabsDropdown: 'omen-dark-dropdown',
    //         },
    //     },
    // };
    const { css, recommendedAccessories } = useTemplateTheme(['recommendedAccessories']);
    const { variant = 'light' } = recommendedAccessories || {};
    const classes = {
        tabActive: `omen-${variant}-tab-active`,
        tab: `omen-${variant}-tab`,
        desktopTitle: `omen-${variant}-desktop-title`,
        accordionTitle: `omen-${variant}-accordion-title`,
        tabsDropdown: `omen-${variant}-dropdown`,
    };

    if (!relatedProductsWithAnalytics || relatedProductsWithAnalytics.length < 1) {
        return null;
    }
    const productsLoading = Object.keys(prices).length < relatedProductsWithAnalytics.length;
    const accessories = mapAccessories(
        relatedProductsWithAnalytics,
        prices,
        productInitial,
        getAttachableProductProps,
        cartId,
        list,
        xsellMethod,
    );
    const { products: resortedAccessories } = useDynamicYieldSort(
        'ab:crossSellDYSort',
        `${selectedCatTitle}`,
        //wait till prices are loaded to sort
        productsLoading ? [] : accessories,
        10,
    );
    const translations = {
        buttons: { attach: 'add to cart' },
        title,
    };

    const cats = css ? categories : mapCategoryIcons(categories);

    return (
        <RecommendedAccessories
            translations={translations}
            categories={cats}
            onCategoryChange={onCategoryChange}
            onAdd={onAdd(accessories)}
            addToCardTimeout={3000}
            products={resortedAccessories.length > 0 ? resortedAccessories : accessories}
            productsLoading={productsLoading}
            className={`pdp-recommended-accessories-section${css ? ` ${css}` : ''}`}
            classes={classes}
        />
    );
};

export default ({ id, pdpAccessories, productInitial }) => {
    const { categories, products = {}, title } = pdpAccessories || {};
    const [firstCat] = categories || [];
    const [selectCat, setSelectCat] = useState(firstCat ? firstCat.id : null);

    useEffect(() => {
        if (categories && categories[0]) {
            setSelectCat(categories[0].id);
        }
    }, [categories]);
    if (!pdpAccessories || Object.keys(products).length < 1) {
        return null;
    }

    return (
        <section id={id}>
            <AccessoriesComponent
                title={title}
                relatedProducts={products[selectCat]}
                categories={categories}
                onCategoryChange={(index, id) => {
                    setSelectCat(id);
                }}
                productInitial={productInitial}
                selectedCat={selectCat}
            />
        </section>
    );
};
